import { useCallback, useEffect, useMemo, useState } from "react";
import {
  fetchPlanningMessages,
  fetchPublishQueueStatus,
  hideFeedMessage,
  publishAllQueued,
  publishQueueNow,
  type PlanningMessage,
  type PublishQueueStatus,
} from "@/api/admin";
import type { FeedMessage } from "@/types";
import { buildFeedRows, mergeById } from "@/utils/feedRows";
import { messageIdKey } from "@/utils/messageId";
import { sortFeedMessages } from "@/utils/feedSort";
import { MessageBubble } from "./MessageBubble";

function toFeedMessage(m: PlanningMessage): FeedMessage {
  return {
    id: m.id,
    date: m.date,
    author: m.author,
    text: m.text,
    reply_to: m.reply_to,
    author_kind: m.author_kind,
    source: m.source,
  };
}

interface Props {
  refreshToken?: number;
}

export function AdminPlanningFeed({ refreshToken = 0 }: Props) {
  const [messages, setMessages] = useState<PlanningMessage[]>([]);
  const [status, setStatus] = useState<PublishQueueStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [publishBusy, setPublishBusy] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [plan, st] = await Promise.all([
        fetchPlanningMessages(),
        fetchPublishQueueStatus(),
      ]);
      setMessages(plan.messages);
      setStatus(st);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload, refreshToken]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      fetchPlanningMessages()
        .then((plan) => setMessages(plan.messages))
        .catch(() => {});
      fetchPublishQueueStatus()
        .then(setStatus)
        .catch(() => {});
    }, 30_000);
    return () => window.clearInterval(timer);
  }, []);

  const feedMessages = useMemo(
    () => sortFeedMessages(messages.map(toFeedMessage)),
    [messages]
  );
  const byId = useMemo(() => mergeById(new Map(), feedMessages), [feedMessages]);
  const rows = useMemo(() => buildFeedRows(feedMessages), [feedMessages]);

  const handleDelete = async (id: string) => {
    if (
      !window.confirm(
        "Убрать сообщение из очереди? Оно не попадёт в публичную ленту."
      )
    ) {
      return;
    }
    const key = messageIdKey(id);
    setDeletingId(key);
    setError(null);
    try {
      await hideFeedMessage(key);
      setMessages((prev) => prev.filter((m) => messageIdKey(m.id) !== key));
      const st = await fetchPublishQueueStatus();
      setStatus(st);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setDeletingId(null);
    }
  };

  const enqueuedLabel = (id: string) => {
    const m = messages.find((x) => messageIdKey(x.id) === messageIdKey(id));
    if (!m?.enqueued_at) return null;
    const d = m.enqueued_at.replace("T", " ").slice(0, 16);
    const src = m.source_date?.replace("T", " ").slice(0, 16);
    return src ?
        `В очереди с ${d} · в чате ${src}`
      : `В очереди с ${d}`;
  };

  return (
    <section className="admin-section admin-planning">
      {status ? (
        <div className="admin-queue-stats">
          <p>
            <strong>В планировании:</strong> {status.feed_queued_count}
            {" · "}
            <strong>Уже в ленте:</strong> {status.feed_published_count}
          </p>
          <p className="admin-muted">
            Авто-публикация каждые {status.queue_interval_minutes} мин (
            {status.night_hours} — пауза). После публикации — внизу публичной
            ленты с текущим временем.
          </p>
          <div className="admin-planning-actions">
            <button
              type="button"
              className="admin-btn"
              disabled={publishBusy || messages.length === 0}
              onClick={async () => {
                setPublishBusy(true);
                try {
                  await publishAllQueued();
                  await reload();
                } catch (e) {
                  setError(e instanceof Error ? e.message : String(e));
                } finally {
                  setPublishBusy(false);
                }
              }}
            >
              Опубликовать всё
            </button>
            <button
              type="button"
              className="admin-btn approve"
              disabled={
                publishBusy || messages.length === 0 || status.is_night_now
              }
              onClick={async () => {
                setPublishBusy(true);
                try {
                  await publishQueueNow();
                  await reload();
                } catch (e) {
                  setError(e instanceof Error ? e.message : String(e));
                } finally {
                  setPublishBusy(false);
                }
              }}
            >
              Опубликовать следующее
            </button>
          </div>
        </div>
      ) : null}

      {error ? <p className="admin-error">{error}</p> : null}
      {loading ? <p className="admin-muted">Загрузка…</p> : null}

      {!loading && messages.length === 0 ? (
        <p className="admin-muted">
          Очередь пуста. Импортируйте JSON с галочкой «очередь за последний месяц» —
          сообщения появятся здесь.
        </p>
      ) : null}

      <div className="admin-planning-feed">
        {rows.map((row) => {
          if (row.kind === "date") {
            return (
              <div className="date-separator" key={row.key}>
                <span>{row.label}</span>
              </div>
            );
          }
          const msg = row.message;
          const reply = msg.reply_to
            ? byId.get(messageIdKey(msg.reply_to))
            : undefined;
          const meta = enqueuedLabel(msg.id);
          return (
            <div key={row.key} className="admin-planning-item">
              {meta ? (
                <p className="admin-planning-meta">{meta}</p>
              ) : null}
              <MessageBubble
                message={msg}
                replyTo={reply}
                adminMode
                deleteBusy={deletingId === messageIdKey(msg.id)}
                onDeleteMessage={() => handleDelete(msg.id)}
              />
            </div>
          );
        })}
      </div>
    </section>
  );
}
